import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminApplicantList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const APPLICANTS_PAGE_SIZE = 30;
const applicantSortOptions = [
  { value: 'updated-desc', label: '更新日が新しい順' },
  { value: 'management-status', label: '管理状態順' },
  { value: 'applicant-type-asc', label: '問合せ/申込 昇順' },
  { value: 'applicant-type-desc', label: '問合せ/申込 降順' },
  { value: 'source-asc', label: '関連元昇順' },
  { value: 'source-desc', label: '関連元降順' },
  { value: 'email-asc', label: 'メールアドレス昇順' },
  { value: 'email-desc', label: 'メールアドレス降順' },
  { value: 'name-asc', label: '氏名昇順' },
  { value: 'name-desc', label: '氏名降順' },
];

const applicantManagementStatusToUi = (isActive) => ({
  label: isActive ? '管理中' : '停止中',
  tone: isActive ? 'published' : 'draft',
});

const applicantTypeToUi = (applicantType) => {
  if (applicantType === 'application') {
    return {
      label: '申込',
      background: 'rgba(124, 42, 46, 0.12)',
      color: '#7c2a2e',
    };
  }

  return {
    label: '問合せ',
    background: 'rgba(89, 102, 116, 0.12)',
    color: '#3d4954',
  };
};

const applicantSourceLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '1.9rem',
  padding: '0.26rem 0.72rem',
  borderRadius: '999px',
  background: 'rgba(89, 102, 116, 0.12)',
  color: '#3d4954',
  fontSize: '0.82rem',
  fontWeight: 600,
  lineHeight: 1.2,
  textDecoration: 'none',
};

const applicantActionColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
};

const applicantActionStackStyle = {
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: '0.28rem',
  whiteSpace: 'nowrap',
};

const applicantTypeColumnStyle = {
  width: '7.5rem',
  minWidth: '7.5rem',
};

const applicantSourceColumnStyle = {
  width: '14rem',
  minWidth: '14rem',
};

const applicantManagementColumnStyle = {
  width: '9rem',
  minWidth: '9rem',
};

const dateColumnStyle = {
  width: '10.5rem',
  minWidth: '10.5rem',
};

const sortApplicantRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'management-status') {
      if (left.isActive === right.isActive) {
        return left.name.localeCompare(right.name, 'ja');
      }
      return left.isActive ? -1 : 1;
    }

    if (sortKey === 'applicant-type-asc') {
      if (left.applicantType === right.applicantType) {
        return left.name.localeCompare(right.name, 'ja');
      }
      return left.applicantType.localeCompare(right.applicantType, 'ja');
    }

    if (sortKey === 'applicant-type-desc') {
      if (left.applicantType === right.applicantType) {
        return left.name.localeCompare(right.name, 'ja');
      }
      return right.applicantType.localeCompare(left.applicantType, 'ja');
    }

    if (sortKey === 'source-asc') {
      if (left.sourceDetailLabel === right.sourceDetailLabel) {
        return left.name.localeCompare(right.name, 'ja');
      }
      return left.sourceDetailLabel.localeCompare(right.sourceDetailLabel, 'ja');
    }

    if (sortKey === 'source-desc') {
      if (left.sourceDetailLabel === right.sourceDetailLabel) {
        return left.name.localeCompare(right.name, 'ja');
      }
      return right.sourceDetailLabel.localeCompare(left.sourceDetailLabel, 'ja');
    }

    if (sortKey === 'email-asc') {
      return left.email.localeCompare(right.email, 'ja');
    }

    if (sortKey === 'email-desc') {
      return right.email.localeCompare(left.email, 'ja');
    }

    if (sortKey === 'name-asc') {
      return left.name.localeCompare(right.name, 'ja');
    }

    if (sortKey === 'name-desc') {
      return right.name.localeCompare(left.name, 'ja');
    }

    const leftDate = Date.parse(left.updatedAt.replace(' ', 'T'));
    const rightDate = Date.parse(right.updatedAt.replace(' ', 'T'));
    const safeLeft = Number.isNaN(leftDate) ? 0 : leftDate;
    const safeRight = Number.isNaN(rightDate) ? 0 : rightDate;
    return safeRight - safeLeft;
  });

  return nextRows;
};

const AdminApplicantsListPage = () => {
  const content = yowanPageContent['/admin/applicants'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [applicantType, setApplicantType] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [sortKey, setSortKey] = useState('updated-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadApplicants = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminApplicantList({
          page: currentPage,
          pageSize: APPLICANTS_PAGE_SIZE,
          q: searchQuery.trim(),
          applicantType,
          isActive: activeStatus,
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError('問い合わせ・申込者を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadApplicants();

    return () => {
      isMounted = false;
    };
  }, [activeStatus, applicantType, content, currentPage, searchQuery]);

  if (!content) {
    return null;
  }

  const sortedRows = sortApplicantRows(rows, sortKey);
  const totalPages = Math.max(1, Math.ceil(apiCount / APPLICANTS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * APPLICANTS_PAGE_SIZE;
  const visibleRows = sortedRows;
  const typeBadgeStyle = (typeUi) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    minHeight: '2rem',
    padding: '0.28rem 0.78rem',
    borderRadius: '999px',
    background: typeUi.background,
    color: typeUi.color,
    fontSize: '0.85rem',
    fontWeight: 600,
    lineHeight: 1.2,
  });
  return (
    <AdminBlogShell activeNavHref="/admin/applicants" routeKey="/admin/applicants">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${apiCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="氏名 / ふりがな / メールアドレス / 電話番号 / 関連内容"
        sortOptions={applicantSortOptions}
      >
        <div className="yowan-admin-toolbar__group">
          <label className="c-form__field yowan-admin-toolbar__field">
            <span className="c-form__label">問合せ/申込</span>
            <select
              className="c-form__control yowan-admin-toolbar__sort"
              value={applicantType}
              onChange={(event) => {
                setApplicantType(event.target.value);
                setCurrentPage(1);
              }}
            >
              {content.filters.applicantType.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="c-form__field yowan-admin-toolbar__field">
            <span className="c-form__label">管理状態</span>
            <select
              className="c-form__control yowan-admin-toolbar__sort"
              value={activeStatus}
              onChange={(event) => {
                setActiveStatus(event.target.value);
                setCurrentPage(1);
              }}
            >
              {content.filters.activeStatus.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </AdminListToolbar>
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">氏名 / メールアドレス</th>
              <th scope="col" style={applicantTypeColumnStyle}>問合せ/申込</th>
              <th scope="col" style={applicantSourceColumnStyle}>関連元</th>
              <th scope="col" style={applicantManagementColumnStyle}>管理状態</th>
              <th scope="col" style={dateColumnStyle}>更新日</th>
              <th scope="col" style={applicantActionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const statusUi = applicantManagementStatusToUi(row.isActive);
              const typeUi = applicantTypeToUi(row.applicantType);

              return (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td>
                    <Link
                      className="yowan-admin-resource-table__title"
                      to={appendYowanPreviewQueryIfNeeded(row.detailHref, preservedDomainSearch)}
                    >
                      {row.name || '氏名未設定'}
                    </Link>
                    <p className="yowan-admin-resource-table__subline">{row.email}</p>
                  </td>
                  <td style={applicantTypeColumnStyle}>
                    <span style={typeBadgeStyle(typeUi)}>
                      <span>{typeUi.label}</span>
                    </span>
                  </td>
                  <td style={applicantSourceColumnStyle}>
                    {row.sourceDetailHref ? (
                      <Link
                        style={applicantSourceLinkStyle}
                        to={appendYowanPreviewQueryIfNeeded(row.sourceDetailHref, preservedDomainSearch)}
                      >
                        {row.sourceDetailLabel}
                      </Link>
                    ) : (
                      <>
                        <p className="yowan-admin-resource-table__title">{row.sourceDetailLabel || '-'}</p>
                        <p className="yowan-admin-resource-table__subline">{row.sourceKindLabel || '-'}</p>
                      </>
                    )}
                  </td>
                  <td style={applicantManagementColumnStyle}>
                    <span
                      className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}
                    >
                      {statusUi.label}
                    </span>
                  </td>
                  <td style={dateColumnStyle}>{row.updatedAt || '-'}</td>
                  <td style={applicantActionColumnStyle}>
                    <div
                      className="yowan-admin-resource-table__actions"
                      style={applicantActionStackStyle}
                    >
                      <Link to={appendYowanPreviewQueryIfNeeded(row.detailHref, preservedDomainSearch)}>
                        詳細
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && visibleRows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  {loadError || '問い合わせ・申込者はまだありません。'}
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={7}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminApplicantsListPage;
